import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { withTheme } from 'styled-components';
import { Button } from 'components/Button';
import { ContainerWrapper } from 'components/Card/cardStyle';
import Container from 'components/Container';
import ContainerHeader from 'components/ContainerHeader';
import { AutoSuggest } from 'components/Form/AutoSuggest';
import { SearchInput } from 'components/Form/SearchInput';
import { HelpModals } from 'components/HelpModals';
import Icon from 'components/Icon';
import LineHeightText from 'components/LineHeightText';
import { ConfirmModal } from 'components/Modal';
import Pagination from 'components/Pagination';
import { Table } from 'components/Table';
import Text from 'components/Text';
import { TransactionModal } from 'components/Transactions';
import {
  withEventYearFilterContext,
  YearDropdown,
} from 'components/YearDropdown';
import { useUpdateQueryString } from 'hooks/useSearchInput';
import { GET_ACCOUNTS } from 'pages/app/Credits/gql/queries/get-accounts';
import { useGetUserCreditsAdmin } from 'pages/app/Users/gql/queries/useGetUserCreditsAdmin';
import { TitleContainer } from 'pages/track/Contact/style';
import { logDevError } from 'shared/alerts';
import { IssueCreditModal } from '../issueCreditModal';
import ExportOpenCreditsReport from './ExportOpenCredits/ExportOpenCreditsReport';
import { PrintOpenCreditsReport } from './ExportOpenCredits/PrintOpenCreditsReport';
import ExpireCredit from './gql/expire-credit';
import { useGetUserOpenCreditsByAccount } from './gql/get-user-open-credits-by-account';

export const CreditButton = styled.div`
  padding: 0;
  margin-left: auto;
  margin-right: 40px;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: 0;
  }
`;

const FilterContainer = styled.div`
  min-width: 150px;
`;

const OpenCreditsDetail = withEventYearFilterContext((props) => {
  const { expireCredit, theme, location, match } = props;
  const [credits, setCredits] = useState();
  const [count, setCount] = useState();
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const { id, id2 } = useParams();

  // Determine if this is admin context (only one id) or account context (two ids)
  const isAdminContext = !id2;
  const userId = isAdminContext ? parseInt(id) : parseInt(id2);
  const accountId = isAdminContext ? null : parseInt(id);

  const thisYear = Number(moment().format('YYYY'));
  const [year, setYear] = useState(thisYear);

  const [showIssueCredit, setShowIssueCredit] = useState(false);
  const [shouldDisplayDeleteConfirmation, setShouldDisplayDeleteConfirmation] =
    useState(false);
  const [credit, setCredit] = useState();

  const [currentTransaction, setCurrentTransaction] = useState(null);

  // Fetch accounts for admin filter
  const { data: accountsData } = useQuery(GET_ACCOUNTS, {
    variables: {
      getAccountsInput: {},
    },
    skip: !isAdminContext,
    fetchPolicy: 'cache-and-network',
  });

  // Use appropriate hook based on context
  const accountHookResult = useGetUserOpenCreditsByAccount();
  const adminHookResult = useGetUserCreditsAdmin(selectedAccountId);

  const { res, loading } = isAdminContext ? adminHookResult : accountHookResult;

  const { pathname } = useLocation();
  const userType = pathname?.split('/').includes('admin-track')
    ? 'track'
    : pathname?.split('/').includes('admin-employee')
    ? 'employee'
    : 'admin';

  useEffect(() => {
    if (isAdminContext) {
      if (res && res.getUserCreditsAdmin) {
        setCredits(res?.getUserCreditsAdmin?.results);
        setCount(res?.getUserCreditsAdmin?.count);
      }
    } else {
      if (res && res.getUserOpenCreditsByAccount) {
        setCredits(res?.getUserOpenCreditsByAccount?.results);
        setCount(res?.getUserOpenCreditsByAccount?.count);
      }
    }
  }, [res, isAdminContext]);

  const updateQueryString = useUpdateQueryString();

  const handleChange = (e) => setSearch(e.target.value);

  const handleBlur = (e) => {
    updateQueryString(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === 13) {
      updateQueryString(e.target.value);
    }
  };

  const columns = [
    {
      label: 'Ticket Holder',
      key: 'ticketHolder',
    },
    ...(isAdminContext
      ? [
          {
            label: 'Account',
            key: 'account',
          },
        ]
      : []),
    {
      label: 'Credit Total',
      key: 'credit',
    },
    {
      label: 'Credit #',
      key: 'creditNum',
    },
    {
      label: 'Issued',
      key: 'issued',
    },
    {
      label: 'Issued By',
      key: 'issuedBy',
    },
    {
      label: 'Original Total',
      key: 'originalTotal',
    },
    {
      label: 'Track Credit',
      key: 'trackCredit',
    },
    {
      label: 'Fee Credit',
      key: 'feeCredit',
    },
    {
      label: 'Purchase',
      key: 'purchase',
    },
    {
      label: '',
      key: 'actions',
    },
  ];

  const urlParams = new URLSearchParams(window.location.search);
  const currentPage = parseInt(urlParams.get('page'));

  function renderRows(credit) {
    const {
      id,
      user,
      amount,
      date,
      issued_by,
      original_amount,
      track_credit,
      fee_credit,
      transaction,
      account,
    } = credit;

    const userName = user.formatted_name;
    const issuer = issued_by.formatted_name;

    return {
      ticketHolder: userName ? (
        <Link
          to={
            'admin' === userType
              ? `/admin/users/transactions/${user.id}`
              : `/admin-${userType}/users/transactions/${user.id}`
          }
          style={{
            color: theme.colors.primary,
          }}
        >
          <LineHeightText>{userName}</LineHeightText>
        </Link>
      ) : (
        ''
      ),
      ...(isAdminContext
        ? {
            account: account?.business_dba || '-',
          }
        : {}),
      credit: amount.toString(),
      creditNum: id,
      issued: date,
      issuedBy: issuer,
      originalTotal: original_amount,
      trackCredit: track_credit,
      feeCredit: fee_credit,
      purchase:
        transaction && transaction.id ? (
          <Link
            to={
              'admin' === userType
                ? `/admin/transactions/detail/${transaction.id}`
                : `/admin-${userType}/transactions/detail/${transaction.id}`
            }
            style={{
              color: theme.colors.primary,
            }}
          >
            <LineHeightText>View</LineHeightText>
          </Link>
        ) : (
          'N/A'
        ),
      actions: (
        <>
          <Icon
            onClick={() => {
              setCredit({ id: id, user_id: user.id });
              setShouldDisplayDeleteConfirmation(true);
            }}
            size={18}
            color={props.theme.colors.primary}
            icon="calendar-4"
          />
        </>
      ),
    };
  }

  async function handleCreditConfirm() {
    try {
      const response = await expireCredit(credit.id, {
        account_id: Number(accountId),
        user_id: Number(userId),
        year: year,
      });
      if (response && !response.errors) {
        toast.success('Credit expired successfully');
        setShouldDisplayDeleteConfirmation(false);
      } else {
        toast.error('Error Expiring Credit');
      }
    } catch (error) {
      logDevError(error);
      toast.error('Error Expiring Credit');
    }
  }

  return loading ? (
    <div />
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'Row',
          justifyContent: 'end',
          marginTop: 20,
          marginRight: 40,
        }}
      >
        <ExportOpenCreditsReport headerText={''} year={year} />

        <PrintOpenCreditsReport headerText={''} year={year} />

        <HelpModals />
      </div>
      <Container>
        <ContainerHeader>
          <ContainerWrapper>
            <TitleContainer>
              <Text
                type="heading"
                color="#3C4144"
                inlineStyle={{ marginRight: 25, marginTop: 10 }}
              >
                Open Credits
              </Text>
              {isAdminContext && (
                <FilterContainer style={{ marginRight: 15 }}>
                  <AutoSuggest
                    placeholder="All Accounts"
                    options={[
                      { value: null, label: 'All Accounts' },
                      ...(accountsData?.getAccounts?.results || []).map(
                        (account) => ({
                          value: account.id,
                          label:
                            account.business_dba || `Account ${account.id}`,
                        })
                      ),
                    ]}
                    value={
                      selectedAccountId
                        ? {
                            value: selectedAccountId,
                            label:
                              accountsData?.getAccounts?.results?.find(
                                (acc) => acc.id === selectedAccountId
                              )?.business_dba || `Account ${selectedAccountId}`,
                          }
                        : { value: null, label: 'All Accounts' }
                    }
                    onChange={(option) =>
                      setSelectedAccountId(option?.value || null)
                    }
                  />
                </FilterContainer>
              )}
              <FilterContainer>
                <YearDropdown
                  showLabel={false}
                  onSelect={(year) => setYear(year)}
                />
              </FilterContainer>
              <div style={{ marginLeft: 15 }}>
                <SearchInput
                  placeholder="Search Credits"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyPress}
                  value={search}
                />
              </div>
            </TitleContainer>
            <CreditButton>
              <Button
                onClick={() => setShowIssueCredit(true)}
                buttonStyle={{ height: 35 }}
              >
                {'Issue Credit'}
              </Button>
            </CreditButton>
          </ContainerWrapper>
        </ContainerHeader>
        <div style={{ padding: 2 }}>{props.children}</div>
        {!credits ? (
          <div />
        ) : (
          <div style={{ padding: 20 }}>
            <Table items={credits} columns={columns} renderRows={renderRows} />
            <Pagination
              count={count}
              perPage={15}
              currentPage={currentPage || 1}
            />
          </div>
        )}
      </Container>
      <IssueCreditModal
        setIsVisible={setShowIssueCredit}
        isVisible={showIssueCredit}
        isTrackAdmin={false}
        vieEmployeeSearch={false}
        customerId={userId ?? ''}
        credits={credits}
        setCredits={setCredits}
      />

      <ConfirmModal
        confirmText="Yes"
        cancelText="Cancel"
        hideModal={() => setShouldDisplayDeleteConfirmation(false)}
        isVisible={shouldDisplayDeleteConfirmation}
        onConfirm={() => handleCreditConfirm()}
        title="Are you sure you want to expire this credit?"
      />

      {currentTransaction ? (
        <TransactionModal
          admin={location.pathname.includes('/admin/transactions/')}
          match={match}
          currentTransaction={currentTransaction}
          close={() => {
            setCurrentTransaction(null);
          }}
        />
      ) : null}
    </>
  );
});

export default withTheme(ExpireCredit(OpenCreditsDetail));
